import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Hourglass } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const usePaymentStatus = (sessionId) => {
  const [state, setState] = useState(sessionId ? "loading" : "paid");
  const [device, setDevice] = useState(null);
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false, timer, tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") { setDevice(r.data.device); return setState("paid"); }
      } catch { /* keep trying */ }
      if (++tries >= 8) return !cancelled && setState("pending");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId]);
  return { state, device };
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { state, device } = usePaymentStatus(params.get("session_id"));

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface p-6 text-center" data-testid="payment-success-page">
      <img src="/assets/logo-full-sm.png" alt="BalkanHD Play" className="mb-2 h-24 w-24 object-contain" />
      {state === "loading" && (
        <>
          <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-brand border-t-transparent" />
          <h1 className="mt-3 text-3xl font-bold" data-testid="payment-title">Potvrđujemo uplatu…</h1>
          <p className="max-w-[340px] text-sm leading-5 text-muted2">Molimo sačekajte trenutak.</p>
        </>
      )}
      {state === "paid" && (
        <div className="fade-up flex flex-col items-center gap-3">
          <CheckCircle size={72} weight="fill" className="text-ok" />
          <h1 className="mt-3 text-3xl font-bold" data-testid="payment-title">Čestitamo! Uplata je uspešna</h1>
          <p className="max-w-[380px] text-sm leading-6 text-txt2" data-testid="payment-text">
            Hvala vam što ste izabrali BalkanHD Play! Vaš uređaj je aktiviran. Potvrda je poslata na vaš email. Otvorite (ili ponovo pokrenite) aplikaciju BalkanHD Play — biće otključana.
          </p>
          {device && (
            <p className="rounded-xl border border-line bg-surface2 px-4 py-3 text-sm font-semibold tracking-wider" data-testid="payment-device">
              {device.device_key} • {device.plan === "lifetime" ? "Doživotno" : device.days_left != null ? `još ${device.days_left} dana` : "Aktivan"}
            </p>
          )}
        </div>
      )}
      {state === "pending" && (
        <div className="fade-up flex flex-col items-center gap-3">
          <Hourglass size={72} weight="fill" className="text-warn" />
          <h1 className="mt-3 text-3xl font-bold" data-testid="payment-title">Uplata se obrađuje</h1>
          <p className="max-w-[360px] text-sm leading-6 text-txt2" data-testid="payment-text">
            Uplata još nije potvrđena. Ako ste platili, aktivacija stiže za koji minut — ponovo pokrenite aplikaciju. Potvrda će vam stići na email.
          </p>
        </div>
      )}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/activate-web" data-testid="success-continue" className="btn-primary">Nazad na aktivaciju</Link>
        <Link to="/landing" data-testid="success-home" className="btn-ghost">Početna</Link>
      </div>
    </div>
  );
}
